import { getConfig } from '../config/config';
import { getKeycloakInstance } from '../config/keycloak';
import { UserDetails, Credential } from '../constants/types';
import { sortByProperty } from '../helpers/sorting-helper';
import {
  getInvitationEmailLifeSpan,
  getOnBoardingRedirectUrl,
  getTenant,
  getkeycloakClientName,
} from '../utils/getKeycloakConfig';
import makeRequest from './makeRequest';

const fetchUsers = async (queryParam?: string): Promise<UserDetails[]> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users?max=-1${queryParam || ''}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return sortByProperty(data, 'firstName', 'lastName');
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchUserById = async (id: string): Promise<UserDetails> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${id}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return data;
  } catch (error: any) {
    throw error?.response;
  }
};

const createUser = async (payload: {}): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('POST', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const updateUser = async (userId: string, payload: {}): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('PUT', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const sendUpdateAccountEmail = async (userId: string): Promise<void> => {
  const lifespan = getInvitationEmailLifeSpan();
  let lifeSpanQueryParam = '';
  if (lifespan) {
    lifeSpanQueryParam = `&lifespan=${lifespan}`;
  }
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}/execute-actions-email?client_id=${getkeycloakClientName()}&redirect_uri=${getOnBoardingRedirectUrl()}${lifeSpanQueryParam}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };
  const payload = ['UPDATE_PASSWORD'];

  try {
    await makeRequest('PUT', url, payload, headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const fetchUserCredentials = async (userId: string): Promise<Credential[]> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}/credentials`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('GET', url, '', headers);
    return Array.isArray(data) ? data : [];
  } catch (error: any) {
    throw error?.response;
  }
};

const deleteUserCredential = async (
  userId: string,
  credentialId: string,
): Promise<void> => {
  const url = `${
    getConfig().keycloakApiEndpoint
  }/auth/admin/realms/${getTenant()}/users/${userId}/credentials/${credentialId}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    await makeRequest('DELETE', url, '', headers);
  } catch (error: any) {
    throw error?.response;
  }
};

const removeUserCredentialByType = async (
  userId: string,
  credentialType: string,
): Promise<boolean> => {
  const credentials = await fetchUserCredentials(userId);
  const credential = credentials.find(cred => cred.type === credentialType);

  if (credential?.id) {
    await deleteUserCredential(userId, credential.id);
    return true;
  }

  return false;
};

const deletePermissionsByPrincipal = async (
  principal: string,
): Promise<boolean> => {
  const url = `${
    getConfig().excelEngineApiEndpoint
  }/api/v1/entitypermission/removeByPrincipal/${principal}`;
  const headers = {
    Authorization: `Bearer ${getKeycloakInstance()?.token}`,
  };

  try {
    const { data } = await makeRequest('DELETE', url, '', headers);
    return data?.status?.toLowerCase() === 'success';
  } catch {
    //do nothing
    return false;
  }
};

export {
  fetchUsers,
  fetchUserById,
  createUser,
  updateUser,
  sendUpdateAccountEmail,
  fetchUserCredentials,
  deleteUserCredential,
  removeUserCredentialByType,
  deletePermissionsByPrincipal,
};
